/**
 * The desk's league mode (DSPRT-474). The NBA desk is mounted for /gleague
 * too: the G League is the same game read off the same provider in the same
 * shapes, so Scores, the box score, Standings, a club's and a player's card
 * and the moves list are the NBA's own with the league's data in them. What
 * this module settles once, at load, is which league the address names, where
 * its API lives and how an address inside the desk is spelled.
 */
export const GL_PREFIX = "/gleague";

/** True when the page opened on a /gleague address. Read once: moving between desks is a page load. */
export const GL_MODE: boolean = typeof window !== "undefined" && /^\/gleague(\/|$)/.test(window.location.pathname);

export const PATH_PREFIX: string = GL_MODE ? GL_PREFIX : "";

// Read defensively: the build's node scripts bundle modules that import this one, with no Vite env.
const env = (import.meta as unknown as { env?: { VITE_API_URL?: string } }).env;
export const NBA_API: string = env?.VITE_API_URL ?? "http://localhost:8080/api/v1/nba";
export const GL_API: string = NBA_API.replace(/\/nba\/?$/, "/gleague");

/** The reads that belong to the league shown; a player's own reads stay the NBA's, his ids being the NBA's. */
const LEAGUE_READS = /^\/(scoreboard|schedule|game-days|stream|standings|teams|games|wire|seasons|clubs|affiliates)(\/|\?|$)/;

/** Which API answers a path: the G League's for the league's own reads in G League mode, the NBA's otherwise. */
export function apiFor(path: string): string {
  return GL_MODE && LEAGUE_READS.test(path) ? GL_API : NBA_API;
}

/** The pages that live under the prefix in G League mode; the Wire is called Moves there. */
const PREFIXED = /^\/(team|game|player|scores|standings|wire|moves|affiliates)(\/|\?|#|$)|^\/(\?|#|$)/;

/** An address of this desk as it reads in the league shown: "/team/OSC" is "/gleague/team/OSC" on the G League side. */
export function appHref(href: string): string {
  if (!GL_MODE || href.startsWith(GL_PREFIX) || !PREFIXED.test(href)) return href;
  const moved = href.replace(/^\/wire(?=\/|\?|#|$)/, "/moves");
  return moved === "/" ? GL_PREFIX : GL_PREFIX + moved;
}

/** ESPN's codes for the 31 clubs, its own (RCITY and VALLEY included). */
export const GL_CLUBS: ReadonlySet<string> = new Set([
  "AUS", "CAP", "CLC", "CPS", "CVL", "DEL", "GBO", "GRD", "IWA", "LAK", "LIN", "MCC", "MHU", "MNE", "MXC", "NOB", "OKL", "OSC", "RAP", "RCITY",
  "RGV", "SAN", "SCW", "SLC", "STO", "SXF", "TEX", "VALLEY", "WCB", "WES", "WIS",
]);

export const isGlClub = (abbr: string | null | undefined): boolean => !!abbr && GL_CLUBS.has(abbr.toUpperCase());

/** How the league shown is named in a sentence. */
export const LEAGUE_NAME = GL_MODE ? "G League" : "NBA";

/** The season a G League table starts at (ESPN's year; 2026 is 2025-26): the first one stored. */
export const GL_FIRST_SEASON = 2026;
/**
 * The regular season's length now on: 34 games a club in 2026-27, the 14
 * Tip-Off Tournament games apart, which the table leaves out (GL-08; 2025-26
 * had 36). Only the season now on is measured against it: a past one is final by date.
 */
export const GL_SEASON_GAMES = 34;
